import React, { useCallback, useEffect, useState } from "react";
import { BookmarkPlus, ChevronDown, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/use-confirm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  createStrategyRdRuleBlock,
  deleteStrategyRdRuleBlock,
  listStrategyRdRuleBlocks,
  updateStrategyRdRuleBlock,
  fitStrategyRdRuleBlock,
  type StrategyRdRuleComposer,
} from "../../../services/strategyRdApi";
import type { StrategyRdRuleBlock, StrategyRdRuleVocabulary } from "../../../types/strategyRd";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { strategyRdRuleSourceLabel } from "../../../utils/strategyRdRuleEditor";
import { strategyRdMetaClassName } from "./strategyRdCardShell";

export type StrategyRdMyRulesRole = "stop" | "target" | "entry" | "filter" | "manage";

/** Bumped after any save / rename / delete so every "My rules" menu reloads. */
const blocksChangedEvent = "strategy-rd-rule-blocks-changed";
function announceBlocksChanged() {
  window.dispatchEvent(new Event(blocksChangedEvent));
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Saved rules for one field (§8): "My rules" menu next to the Preset / Custom switch,
 * "Save as my rule", and "Manage my rules". Rules are private to the user.
 */
export function StrategyRdMyRules({
  role,
  composer,
  vocabulary,
  currentRule,
  onApply,
}: {
  role: StrategyRdMyRulesRole;
  composer: StrategyRdRuleComposer | null;
  vocabulary?: StrategyRdRuleVocabulary | null;
  /** The field's current rule (what "Save as my rule" saves); null hides that action. */
  currentRule: Record<string, unknown> | null;
  /** Merge the fitted rule's `template_patch`, and show its readback. */
  onApply: (templatePatch: Record<string, unknown>, readback: string | null) => void;
}): JSX.Element | null {
  const [blocks, setBlocks] = useState<StrategyRdRuleBlock[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [choice, setChoice] = useState<{
    block: StrategyRdRuleBlock;
    choices: Record<string, string[]>;
    picked: Record<string, string>;
  } | null>(null);
  const [useError, setUseError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBlocks(await listStrategyRdRuleBlocks(role));
    } catch {
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    const onChanged = () => setBlocks(null);
    window.addEventListener(blocksChangedEvent, onChanged);
    return () => window.removeEventListener(blocksChangedEvent, onChanged);
  }, []);

  const applyBlock = async (block: StrategyRdRuleBlock, remap?: Record<string, string>) => {
    if (!composer) return;
    setUseError(null);
    try {
      const res = await fitStrategyRdRuleBlock(block.id, composer, remap);
      if (res.ok && res.template_patch) {
        setChoice(null);
        onApply(res.template_patch, res.readback ?? null);
        return;
      }
      const keys = Object.keys(res.choices);
      if (keys.length > 0) {
        setChoice({
          block,
          choices: res.choices,
          picked: Object.fromEntries(keys.map((k) => [k, res.choices[k][0]])),
        });
        return;
      }
      setUseError(res.errors.join(" ") || "This rule doesn't fit this recipe.");
    } catch (err) {
      setUseError(errorMessage(err, "Could not use the rule."));
    }
  };

  if (!composer) return null;

  return (
    <>
      <DropdownMenu onOpenChange={(open) => open && blocks == null && void load()}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" data-strategy-rd-my-rules={role}>
            My rules <ChevronDown className="size-3" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-w-sm">
          {loading ? (
            <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal">
              <Loader2 className="size-3 animate-spin" aria-hidden /> Loading…
            </DropdownMenuLabel>
          ) : blocks && blocks.length > 0 ? (
            blocks.map((b) => (
              <DropdownMenuItem key={b.id} onSelect={() => void applyBlock(b)} className="flex flex-col items-start gap-0">
                <span className="text-sm">{b.name}</span>
                {b.readback ? <span className="text-[11px] text-muted-foreground">{b.readback}</span> : null}
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              No saved rules for this yet.
            </DropdownMenuLabel>
          )}
          <DropdownMenuSeparator />
          {currentRule ? (
            <DropdownMenuItem onSelect={() => setSaveOpen(true)}>
              <BookmarkPlus className="size-3.5" aria-hidden /> Save as my rule…
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => setManageOpen(true)}>
            <Pencil className="size-3.5" aria-hidden /> Manage my rules…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {useError ? <p className="basis-full text-xs text-destructive">{useError}</p> : null}

      {saveOpen && currentRule ? (
        <SaveRuleDialog
          role={role}
          rule={currentRule}
          composer={composer}
          onClose={() => setSaveOpen(false)}
        />
      ) : null}

      {manageOpen ? <ManageRulesDialog onClose={() => setManageOpen(false)} /> : null}

      {choice ? (
        <Dialog open onOpenChange={(open) => !open && setChoice(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">Which confluence?</DialogTitle>
              <DialogDescription>
                &ldquo;{choice.block.name}&rdquo; reads a confluence this recipe has more than once. Pick the
                one it should use.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              {Object.entries(choice.choices).map(([savedKey, keys]) => (
                <fieldset key={savedKey} className="flex flex-col gap-1">
                  <legend className="mb-1 text-xs text-muted-foreground">
                    For {strategyRdRuleSourceLabel(savedKey, vocabulary)}
                  </legend>
                  {keys.map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={`choice-${savedKey}`}
                        checked={choice.picked[savedKey] === k}
                        onChange={() =>
                          setChoice((prev) => (prev ? { ...prev, picked: { ...prev.picked, [savedKey]: k } } : prev))
                        }
                      />
                      {strategyRdRuleSourceLabel(k, vocabulary)}
                      <span className="text-xs text-muted-foreground">({k})</span>
                    </label>
                  ))}
                </fieldset>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline-gray" size="sm" onClick={() => setChoice(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="gradient"
                size="sm"
                onClick={() => void applyBlock(choice.block, choice.picked)}
              >
                Use this one
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

function SaveRuleDialog({
  role,
  rule,
  composer,
  onClose,
}: {
  role: StrategyRdMyRulesRole;
  rule: Record<string, unknown>;
  composer: StrategyRdRuleComposer;
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createStrategyRdRuleBlock({ name: name.trim(), role, rule, composer });
      announceBlocksChanged();
      toast.success("Saved to My rules");
      onClose();
    } catch (err) {
      // 400: duplicate name, or the rule doesn't fit — shown by the name field.
      setError(errorMessage(err, "Could not save the rule."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">Save as my rule</DialogTitle>
          <DialogDescription>Only you can see your rules. Use them in any recipe that has the confluences they read.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          <Input
            autoFocus
            maxLength={80}
            placeholder="e.g. Below the breaker"
            value={name}
            aria-invalid={Boolean(error)}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
            }}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline-gray" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="gradient" size="sm" disabled={saving || !name.trim()} onClick={() => void save()}>
            {saving ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ROLE_LABELS: Record<string, string> = {
  stop: "Stop",
  target: "Target",
  entry: "Entry",
  filter: "Filter",
  manage: "Manage",
};

/** Rename / delete saved rules (all roles). */
function ManageRulesDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [blocks, setBlocks] = useState<StrategyRdRuleBlock[] | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string; error: string | null } | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  useEffect(() => {
    listStrategyRdRuleBlocks(null)
      .then(setBlocks)
      .catch((err) => {
        presentMutationError(err, "Could not load your rules");
        setBlocks([]);
      });
  }, []);

  const rename = async () => {
    if (!editing?.name.trim()) return;
    try {
      const row = await updateStrategyRdRuleBlock(editing.id, { name: editing.name.trim() });
      setBlocks((prev) => (prev ?? []).map((b) => (b.id === row.id ? row : b)));
      setEditing(null);
      announceBlocksChanged();
    } catch (err) {
      setEditing((prev) => (prev ? { ...prev, error: errorMessage(err, "Could not rename.") } : prev));
    }
  };

  const remove = async (b: StrategyRdRuleBlock) => {
    if (!(await confirm({ title: `Delete “${b.name}”?`, description: "This can't be undone." }))) return;
    try {
      await deleteStrategyRdRuleBlock(b.id);
      setBlocks((prev) => (prev ?? []).filter((x) => x.id !== b.id));
      announceBlocksChanged();
    } catch (err) {
      presentMutationError(err, "Could not delete the rule");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">My rules</DialogTitle>
          <DialogDescription>Private to you: there's no sharing yet.</DialogDescription>
        </DialogHeader>
        {blocks == null ? (
          <p className={strategyRdMetaClassName}>Loading…</p>
        ) : blocks.length === 0 ? (
          <p className={strategyRdMetaClassName}>No saved rules yet. Use &ldquo;Save as my rule&rdquo; in any rule editor.</p>
        ) : (
          <ul className="flex max-h-80 flex-col divide-y divide-border overflow-y-auto">
            {blocks.map((b) => (
              <li key={b.id} className="flex items-start gap-2 py-2">
                <div className="min-w-0 flex-1">
                  {editing?.id === b.id ? (
                    <div className="flex flex-col gap-1">
                      <Input
                        autoFocus
                        maxLength={80}
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value, error: null })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void rename();
                          if (e.key === "Escape") setEditing(null);
                        }}
                        onBlur={() => void rename()}
                      />
                      {editing.error ? <p className="text-xs text-destructive">{editing.error}</p> : null}
                    </div>
                  ) : (
                    <p className="text-sm">
                      {b.name}{" "}
                      <span className="text-[11px] text-muted-foreground">· {ROLE_LABELS[b.role] ?? b.role}</span>
                    </p>
                  )}
                  {b.readback ? <p className="text-[11px] text-muted-foreground">{b.readback}</p> : null}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`Rename ${b.name}`}
                  onClick={() => setEditing({ id: b.id, name: b.name, error: null })}
                >
                  <Pencil className="size-3.5" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`Delete ${b.name}`}
                  onClick={() => void remove(b)}
                >
                  <Trash2 className="size-3.5" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
      {confirmDialog}
    </Dialog>
  );
}
